import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight,
  ArrowLeft,
  Sparkles,
  CheckCircle,
  Loader2,
  CreditCard,
  TrendingUp,
  PiggyBank,
  BookOpen,
  Target
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Investor Type Questions (simplified from Survey.jsx)
const INVESTOR_QUESTIONS = [
  {
    id: "q1",
    title: "Risk Tolerance",
    question: "You invest $500. Six months later, the market drops and it's worth $350. How do you feel?",
    options: [
      { value: "a", label: "Sell immediately, I can't handle watching it drop more", scores: { climber: 4, protector: 3 } },
      { value: "b", label: "Very nervous, I'd probably sell soon if it doesn't recover", scores: { climber: 3, learner: 2 } },
      { value: "c", label: "Uncomfortable, but I'd wait and see what happens", scores: { optimizer: 3, builder: 2 } },
      { value: "d", label: "Calm. It's a long-term investment, dips happen", scores: { builder: 4, optimizer: 3 } },
      { value: "e", label: "Excited to buy more, stocks are on sale!", scores: { visionary: 4, opportunist: 3 } }
    ]
  },
  {
    id: "q2",
    title: "Financial Goals",
    question: "What's your primary financial goal right now?",
    options: [
      { value: "a", label: "Build long-term wealth", scores: { builder: 4, optimizer: 2 } },
      { value: "b", label: "Save for a specific purchase (car, trip)", scores: { climber: 3, learner: 2 } },
      { value: "c", label: "Get out of debt", scores: { climber: 4, protector: 3 } },
      { value: "d", label: "Make money quickly", scores: { visionary: 3, opportunist: 4, maverick: 3 } },
      { value: "e", label: "Not sure yet, still figuring it out", scores: { learner: 5 } }
    ]
  },
  {
    id: "q3",
    title: "Investment Timeline",
    question: "When do you plan to use your invested money?",
    options: [
      { value: "a", label: "20+ years from now", scores: { builder: 5, optimizer: 3 } },
      { value: "b", label: "10-20 years", scores: { optimizer: 4, builder: 2 } },
      { value: "c", label: "5-10 years", scores: { climber: 3, optimizer: 2 } },
      { value: "d", label: "Less than 5 years", scores: { protector: 3, climber: 3 } },
      { value: "e", label: "Might need it anytime", scores: { learner: 3, protector: 2 } }
    ]
  },
  {
    id: "q4",
    title: "Windfall Decision",
    question: "You receive an unexpected $2,000 bonus. First thought?",
    options: [
      { value: "a", label: "Straight to investments", scores: { builder: 5, protector: 2 } },
      { value: "b", label: "Finally! I can max out my TFSA", scores: { optimizer: 4, builder: 3 } },
      { value: "c", label: "Time for that vacation I've been wanting!", scores: { maverick: 4, visionary: 2 } },
      { value: "d", label: "Emergency fund first, then we'll see", scores: { climber: 4, protector: 4 } },
      { value: "e", label: "Pay off debt immediately", scores: { climber: 4, protector: 3 } }
    ]
  }
];

// Category Questions (from CategoryQuiz.jsx, simplified)
const CATEGORY_QUESTIONS = [
  {
    id: "c1",
    title: "Debt Situation",
    question: "What best describes your current debt situation?",
    options: [
      { value: "A", label: "I have high-interest debt (credit cards) that's overwhelming", category: "debtFree", weight: 15 },
      { value: "B", label: "I have manageable debt (student loans, car payment)", category: "planSave", weight: 8 },
      { value: "C", label: "I'm debt-free but have little savings", category: "planSave", weight: 10 },
      { value: "D", label: "I'm debt-free with emergency savings ready to invest", category: "startInvesting", weight: 12 }
    ]
  },
  {
    id: "c2",
    title: "Primary Goal",
    question: "What's your #1 financial priority right now?",
    options: [
      { value: "A", label: "Eliminate debt as fast as possible", category: "debtFree", weight: 15 },
      { value: "B", label: "Start investing but I'm a complete beginner", category: "startInvesting", weight: 15 },
      { value: "C", label: "Grow my existing investments", category: "buildWealth", weight: 15 },
      { value: "D", label: "Understand money and finance first", category: "learnFinance", weight: 15 }
    ]
  },
  {
    id: "c3",
    title: "Financial Knowledge",
    question: "How would you rate your investing knowledge?",
    options: [
      { value: "A", label: "Beginner - I don't understand basic terms", category: "learnFinance", weight: 10 },
      { value: "B", label: "Novice - I understand basics but haven't invested", category: "startInvesting", weight: 8 },
      { value: "C", label: "Intermediate - I have investments and understand fundamentals", category: "buildWealth", weight: 8 },
      { value: "D", label: "Advanced - I actively manage my portfolio", category: "buildWealth", weight: 12 }
    ]
  },
  {
    id: "c4",
    title: "Upcoming Expenses",
    question: "Do you have major expenses planned in the next 12 months?",
    options: [
      { value: "A", label: "Yes - Wedding, home purchase, or large expense ($10K+)", category: "planSave", weight: 20 },
      { value: "B", label: "Yes - Trip, car, or medium goal ($3K-$10K)", category: "planSave", weight: 15 },
      { value: "C", label: "Maybe - Should probably plan for something", category: "planSave", weight: 5 },
      { value: "D", label: "No major expenses planned", category: "startInvesting", weight: 3 }
    ]
  }
];

// Investor Types
const INVESTOR_TYPES = {
  builder: { name: "The Strategic Builder", emoji: "🏗️", description: "Patient, research-focused, long-term thinker" },
  optimizer: { name: "The Balanced Optimizer", emoji: "⚖️", description: "Smart risk-taker, diversifies well" },
  visionary: { name: "The Bold Visionary", emoji: "🚀", description: "High-risk tolerance, entrepreneurial mindset" },
  climber: { name: "The Steady Climber", emoji: "🧗", description: "Conservative, values security and consistency" },
  learner: { name: "The Future-Ready Learner", emoji: "📚", description: "Not investing yet, but eager to learn and grow" },
  opportunist: { name: "The Market Opportunist", emoji: "🎯", description: "Watches for deals, timing-focused investor" },
  protector: { name: "The Wealth Protector", emoji: "🛡️", description: "Preservation-first, inflation-conscious saver" },
  maverick: { name: "The Trend Maverick", emoji: "⚡", description: "Follows trends, social-driven investor" }
};

// Category Info
const CATEGORY_INFO = {
  debtFree: { name: "Get Debt-Free", icon: CreditCard, color: "from-red-500 to-red-600", path: "/debt-free" },
  startInvesting: { name: "Start Investing", icon: TrendingUp, color: "from-emerald-500 to-emerald-600", path: "/start-investing" },
  buildWealth: { name: "Build Wealth", icon: PiggyBank, color: "from-amber-500 to-amber-600", path: "/build-wealth" },
  learnFinance: { name: "Learn Finance", icon: BookOpen, color: "from-blue-500 to-blue-600", path: "/learn-finance" },
  planSave: { name: "Plan & Save", icon: Target, color: "from-purple-500 to-purple-600", path: "/plan-save" }
};

const Onboarding = () => {
  const navigate = useNavigate();
  const [stage, setStage] = useState("investor"); // "investor" | "category" | "loading" | "result"
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [investorAnswers, setInvestorAnswers] = useState({});
  const [categoryAnswers, setCategoryAnswers] = useState({});
  const [investorResult, setInvestorResult] = useState(null);
  const [categoryResult, setCategoryResult] = useState(null);

  const totalQuestions = INVESTOR_QUESTIONS.length + CATEGORY_QUESTIONS.length;
  const currentQuestionIndex = stage === "investor" 
    ? currentQuestion 
    : INVESTOR_QUESTIONS.length + currentQuestion;
  const progress = ((currentQuestionIndex + 1) / totalQuestions) * 100;

  const questions = stage === "investor" ? INVESTOR_QUESTIONS : CATEGORY_QUESTIONS;
  const currentQ = questions[currentQuestion];
  const answers = stage === "investor" ? investorAnswers : categoryAnswers;
  const setAnswers = stage === "investor" ? setInvestorAnswers : setCategoryAnswers;

  // Calculate investor type
  const calculateInvestorType = () => {
    const scores = {
      builder: 0, optimizer: 0, visionary: 0, climber: 0,
      learner: 0, opportunist: 0, protector: 0, maverick: 0
    };

    Object.values(investorAnswers).forEach((answer) => {
      if (answer?.scores) {
        Object.entries(answer.scores).forEach(([type, points]) => {
          scores[type] = (scores[type] || 0) + points;
        });
      }
    });

    const sortedScores = Object.entries(scores).sort((a, b) => b[1] - a[1]);
    return sortedScores[0][0];
  };

  // Calculate recommended category
  const calculateCategory = () => {
    const scores = {
      debtFree: 0, startInvesting: 0, buildWealth: 0, learnFinance: 0, planSave: 0
    };

    Object.values(categoryAnswers).forEach((answer) => {
      if (answer?.category && answer?.weight) {
        scores[answer.category] += answer.weight;
      }
    });

    const sortedScores = Object.entries(scores).sort((a, b) => b[1] - a[1]);
    return sortedScores[0][0];
  };

  const handleAnswer = (option) => {
    setAnswers((prev) => ({
      ...prev,
      [currentQ.id]: option
    }));
  };

  const handleNext = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion((prev) => prev + 1);
    } else if (stage === "investor") {
      // Move to category quiz
      const investorType = calculateInvestorType();
      setInvestorResult(investorType);
      localStorage.setItem("investorQuizCompleted", INVESTOR_TYPES[investorType].name);
      setStage("category");
      setCurrentQuestion(0);
    } else {
      // Finish both quizzes
      finishOnboarding();
    }
  };

  const handleBack = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion((prev) => prev - 1);
    } else if (stage === "category") {
      setStage("investor");
      setCurrentQuestion(INVESTOR_QUESTIONS.length - 1);
    }
  };

  const finishOnboarding = async () => {
    setStage("loading");
    
    const category = calculateCategory();
    setCategoryResult(category);
    
    // Save to localStorage
    localStorage.setItem("recommendedCategory", category);
    localStorage.setItem("onboardingComplete", "true");
    
    // Try to save to backend
    try {
      const userData = localStorage.getItem("user_data");
      if (userData) {
        const user = JSON.parse(userData);
        await axios.post(`${API_URL}/api/progress/onboarding`, {
          user_id: user.id || user.email,
          investor_type: investorResult,
          recommended_category: category,
          investor_answers: investorAnswers,
          category_answers: categoryAnswers
        });
      }
    } catch (e) {
      console.error("Error saving onboarding:", e);
    }

    // Show result after brief loading
    setTimeout(() => {
      setStage("result");
    }, 1500);
  };

  // Result screen
  if (stage === "result" && investorResult && categoryResult) {
    const investorData = INVESTOR_TYPES[investorResult];
    const categoryData = CATEGORY_INFO[categoryResult];
    const CategoryIcon = categoryData.icon;

    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-blue-50 py-12 px-4" data-testid="onboarding-result">
        <div className="max-w-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {/* Success Header */}
            <div className="text-center mb-8">
              <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-10 h-10 text-emerald-600" />
              </div>
              <Badge className="bg-emerald-100 text-emerald-700 mb-4">
                Onboarding Complete!
              </Badge>
              <h1 className="text-3xl font-heading font-bold text-navy-700 mb-2">
                Your Personalized Path is Ready
              </h1>
            </div>

            {/* Investor Type Card */}
            <Card className="mb-6 border-0 shadow-lg">
              <CardContent className="p-6">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-16 h-16 bg-emerald-100 rounded-2xl flex items-center justify-center">
                    <span className="text-4xl">{investorData.emoji}</span>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wide">Your Investor Type</p>
                    <h3 className="text-xl font-heading font-bold text-navy-700">{investorData.name}</h3>
                    <p className="text-sm text-slate-600">{investorData.description}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Recommended Category Card */}
            <Card className="mb-6 overflow-hidden border-2 border-emerald-200">
              <div className={`bg-gradient-to-r ${categoryData.color} p-6 text-white`}>
                <Badge className="bg-white/20 text-white border-0 mb-3">
                  Recommended Starting Point
                </Badge>
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
                    <CategoryIcon className="w-8 h-8" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-heading font-bold">{categoryData.name}</h2>
                    <p className="text-white/80">This is where you should focus first</p>
                  </div>
                </div>
              </div>
              <CardContent className="p-6">
                <Button
                  onClick={() => navigate("/dashboard")}
                  className={`w-full bg-gradient-to-r ${categoryData.color} hover:opacity-90 text-white py-6 text-lg rounded-xl`}
                  data-testid="go-to-dashboard-btn"
                >
                  Go to Your Dashboard
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    );
  }

  // Loading screen
  if (stage === "loading") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-emerald-500 animate-spin mx-auto mb-4" />
          <h2 className="text-xl font-heading font-bold text-navy-700 mb-2">
            Creating Your Personalized Dashboard...
          </h2>
          <p className="text-slate-600">Just a moment while we analyze your responses</p>
        </div>
      </div>
    );
  }

  // Quiz screen
  const currentAnswer = answers[currentQ?.id];

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-blue-50 py-12 px-4" data-testid="onboarding-page">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <Badge className={`mb-4 ${stage === "investor" ? "bg-emerald-100 text-emerald-700" : "bg-purple-100 text-purple-700"}`}>
            {stage === "investor" ? "Step 1: Investor Type Quiz" : "Step 2: Category Recommendation"}
          </Badge>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-navy-700 mb-2">
            {stage === "investor" ? "What Kind of Investor Are You?" : "Find Your Starting Point"}
          </h1>
          <p className="text-slate-600 text-sm sm:text-base">
            {stage === "investor" 
              ? "Answer these questions to discover your investing personality"
              : "Help us recommend the best category for your situation"}
          </p>
        </div>

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex justify-between text-sm text-slate-500 mb-2">
            <span>Question {currentQuestionIndex + 1} of {totalQuestions}</span>
            <span>{Math.round(progress)}% complete</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        {/* Question Card */}
        <AnimatePresence mode="wait">
          <motion.div
            key={`${stage}-${currentQuestion}`}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            <Card className="mb-6 border-0 shadow-lg" data-testid="quiz-card">
              <CardHeader>
                <Badge className="w-fit bg-slate-100 text-slate-600 mb-2">
                  {currentQ?.title}
                </Badge>
                <CardTitle className="text-xl font-heading">
                  {currentQ?.question}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <RadioGroup
                  value={currentAnswer?.value || ""}
                  onValueChange={(value) => {
                    const option = currentQ.options.find((o) => o.value === value);
                    handleAnswer(option);
                  }}
                >
                  <div className="space-y-3">
                    {currentQ?.options.map((option) => (
                      <label
                        key={option.value}
                        className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                          currentAnswer?.value === option.value
                            ? "border-emerald-500 bg-emerald-50"
                            : "border-slate-200 hover:border-emerald-300"
                        }`}
                      >
                        <RadioGroupItem value={option.value} />
                        <span className="text-slate-700 text-sm">{option.label}</span>
                      </label>
                    ))}
                  </div>
                </RadioGroup>
              </CardContent>
            </Card>
          </motion.div>
        </AnimatePresence>

        {/* Navigation Buttons */}
        <div className="flex justify-between gap-4">
          <Button
            variant="outline"
            onClick={handleBack}
            disabled={stage === "investor" && currentQuestion === 0}
            className="px-6"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>

          <Button
            onClick={handleNext}
            disabled={!currentAnswer}
            className="bg-emerald-500 hover:bg-emerald-600 text-white px-6"
            data-testid="onboarding-next-btn"
          >
            {stage === "category" && currentQuestion === CATEGORY_QUESTIONS.length - 1 ? (
              <>
                <Sparkles className="w-4 h-4 mr-2" />
                See My Results
              </>
            ) : (
              <>
                Next
                <ArrowRight className="w-4 h-4 ml-2" />
              </>
            )}
          </Button>
        </div>

        {/* Stage Indicators */}
        <div className="flex justify-center gap-2 mt-8">
          <div className={`w-3 h-3 rounded-full ${stage === "investor" ? "bg-emerald-500" : "bg-emerald-200"}`} />
          <div className={`w-3 h-3 rounded-full ${stage === "category" ? "bg-emerald-500" : "bg-emerald-200"}`} />
        </div>
      </div>
    </div>
  );
};

export default Onboarding;
